import { useEffect, useRef } from 'react';
import Phaser from 'phaser';

import { createGameConfig } from '@/features/letterParty/game/config';
import LetterPartyScene from '@/features/letterParty/game/scenes/LetterPartyScene';
import { syncLetterPartyCanvasSize } from '@/features/letterParty/game/utils/canvasSize';

import type { RefObject } from 'react';
import type { LetterPartyTheme } from '@/features/letterParty/types';
import type { LetterPartyBoardState } from '@/features/letterParty/game/scenes/LetterPartyScene';

const LETTER_PARTY_GAME_DESTROY_EVENT = Phaser.Core.Events.DESTROY;

type UseLetterPartyPhaserGameParams = {
	boardState: LetterPartyBoardState;
	containerRef: RefObject<HTMLDivElement | null>;
	onSwipeDirection: (direction: LetterPartyBoardState['currentDirection']) => void;
	theme: LetterPartyTheme;
};

let pendingLetterPartyGameDestroy: Promise<void> | null = null;

function releaseWebGlContext(game: Phaser.Game): void {
	if (game.renderer.type !== Phaser.WEBGL) {
		return;
	}

	const renderer = game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
	const loseContext = renderer.gl?.getExtension('WEBGL_lose_context');

	loseContext?.loseContext();
}

function destroyLetterPartyGame(game: Phaser.Game): void {
	let resolveDestroy: () => void = () => undefined;
	const destroyPromise = new Promise<void>((resolve) => {
		resolveDestroy = resolve;
	});

	pendingLetterPartyGameDestroy = destroyPromise;

	game.events.once(LETTER_PARTY_GAME_DESTROY_EVENT, () => {
		if (pendingLetterPartyGameDestroy === destroyPromise) {
			pendingLetterPartyGameDestroy = null;
		}

		resolveDestroy();
	});

	releaseWebGlContext(game);
	game.destroy(true);
}

export function useLetterPartyPhaserGame({
	boardState,
	containerRef,
	onSwipeDirection,
	theme,
}: UseLetterPartyPhaserGameParams): void {
	const gameRef = useRef<Phaser.Game | null>(null);
	const sceneRef = useRef<LetterPartyScene | null>(null);
	const boardStateRef = useRef(boardState);
	const onSwipeDirectionRef = useRef(onSwipeDirection);

	useEffect(() => {
		boardStateRef.current = boardState;
		sceneRef.current?.updateBoard(boardState);
	}, [boardState]);

	useEffect(() => {
		onSwipeDirectionRef.current = onSwipeDirection;
	}, [onSwipeDirection]);

	useEffect(() => {
		let isMounted = true;
		let handleResize: (() => void) | null = null;
		let resizeObserver: ResizeObserver | null = null;

		const mountGame = async () => {
			if (pendingLetterPartyGameDestroy) {
				await pendingLetterPartyGameDestroy;
			}

			const container = containerRef.current;
			if (!isMounted || !container || gameRef.current) {
				return;
			}

			const scene = new LetterPartyScene((direction) => onSwipeDirectionRef.current(direction), theme);
			sceneRef.current = scene;

			const game = new Phaser.Game(createGameConfig(container, scene));
			gameRef.current = game;
			syncLetterPartyCanvasSize(game, container);
			scene.updateBoard(boardStateRef.current);

			handleResize = () => {
				if (gameRef.current && container.isConnected) {
					syncLetterPartyCanvasSize(gameRef.current, container);
					sceneRef.current?.updateBoard(boardStateRef.current);
				}
			};

			resizeObserver = new ResizeObserver(handleResize);
			resizeObserver.observe(container);
		};

		mountGame();

		return () => {
			isMounted = false;
			resizeObserver?.disconnect();

			if (gameRef.current) {
				destroyLetterPartyGame(gameRef.current);
			}

			gameRef.current = null;
			sceneRef.current = null;
		};
	}, [containerRef, theme]);
}
